import {
  Button,
  Card,
  CardContent,
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  type ComboboxOption,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Dropzone,
  type DropzoneFiles,
  Icon,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@hurdl/design-system';
import { AlertTriangle, ArrowRight, Check, FileText, X } from 'lucide-react';
import {
  useId,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';

import {
  type AllergenCorrectionEdit,
  type AllergenPresence,
  type AllergenValue,
  type Category,
  type ChangeDecision,
  type FieldChange,
  type FieldValue,
  type ImageChangeView,
  type ImageMediaType,
  type ReviewedItem,
  type ReviewItem,
  type ReviewSubmission,
} from './types.js';

/**
 * The shared review-and-edit sections of the menu-workspace (menus-spec R13, R15, R16).
 *
 * These are the self-contained editor components the earlier single-page workspace held
 * — the queued-item editor, the reviewed-item editor, and the sections, pickers, tables
 * and helpers they compose. They are extracted here so the first-class review and menus
 * routes each reuse exactly the editor that already worked and is token-correct, rather
 * than reimplementing it: the focused single-item review page renders a {@link
 * QueueEditor}, and the restaurant menu page renders a {@link ReviewedEditor} for a
 * chosen reviewed item.
 *
 * Each editor takes its item, the Restaurant's managed Categories and an `onSubmit`
 * callback; a submission is a {@link ReviewSubmission} the page's operations sequence
 * into the granular backend writes. The sections hold no menu data and reach nothing —
 * the one network-adjacent act, reading a chosen image's bytes, is the browser's own
 * `FileReader` and touches no address.
 */

/** The allergens a before/after shows, in a reader's order (the platform's set). */
export const ALLERGEN_ORDER = [
  'wheat',
  'rye',
  'barley',
  'oats',
  'spelt',
  'kamut',
  'soya',
  'nuts',
  'peanuts',
  'sesame',
  'milk',
  'eggs',
  'fish',
  'crustaceans',
  'celery',
  'sulphurDioxideAndSulphites',
  'mustard',
  'molluscs',
  'lupin',
] as const;

/** The nutrients the editor shows and sets, in the order a label reads. */
export const NUTRIENT_FIELDS = [
  { key: 'energyKcal', label: 'Energy (kcal)' },
  { key: 'fatG', label: 'Fat (g)' },
  { key: 'saturatesG', label: 'Saturates (g)' },
  { key: 'carbohydrateG', label: 'Carbohydrate (g)' },
  { key: 'sugarsG', label: 'Sugars (g)' },
  { key: 'proteinG', label: 'Protein (g)' },
  { key: 'saltG', label: 'Salt (g)' },
] as const;

/** The four presence values the allergen chooser offers. */
export const ALLERGEN_VALUES: ReadonlyArray<AllergenValue> = [
  'contains',
  'may-contain',
  'none',
  'absent',
];

/** The image media types the Dropzone accepts, mapped from a chosen File's type. */
const ACCEPTED_IMAGE_TYPES: ReadonlyArray<ImageMediaType> = [
  'image/png',
  'image/jpeg',
  'image/webp',
];

/** How an allergen presence reads in the before/after, an absent value as a dash. */
export function presence(value: AllergenPresence | undefined): string {
  return value ?? '\u2014';
}

/** The size suffix shown beside a sized item's name, or empty. */
export function sizeSuffix(sizeVariant: string | undefined): string {
  return sizeVariant === undefined ? '' : ` (${sizeVariant})`;
}

/**
 * The control that shows the source PDF on the page, without navigating away.
 *
 * The trigger is the primary call a reviewer makes beside the form — they read the
 * booklet to decide — so it is a `primary` button, and it is rendered inside the editor
 * frame's sticky header ({@link ItemEditorFrame}) rather than in a page header a modal
 * could cover. That placement is the fix for the button vanishing while the edit form
 * was on screen: the edit form is no longer a modal over the page, and the trigger
 * travels with the form's own header, so it stays reachable at every scroll position on
 * either viewport.
 */
export function ViewBookletDialog({
  bookletUrl,
}: {
  readonly bookletUrl: string;
}): ReactElement {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="primary" className="w-full md:w-auto">
          <Icon icon={FileText} size="small" />
          View PDF
        </Button>
      </DialogTrigger>
      {/*
        A large dialog surface so the booklet is readable: the content column is a
        flex column filling the dialog, and the iframe grows to fill it, so the PDF
        occupies the whole surface rather than a cramped strip.
      */}
      <DialogContent size="large" className="flex h-[80vh] flex-col">
        <DialogHeader>
          <DialogTitle>Allergen booklet</DialogTitle>
        </DialogHeader>
        {/*
          A native iframe inside the Dialog: the panel owns no PDF renderer, and the
          browser's own is exactly right for a reviewer. The title names it for
          assistive technology; the src is the same-origin path the panel supplies,
          never a cross-origin literal. `grow` with `h-full w-full` fills the surface.
        */}
        <iframe
          title="Allergen booklet PDF"
          src={bookletUrl}
          className="h-full w-full grow rounded-hurdl-medium border border-border"
        />
      </DialogContent>
    </Dialog>
  );
}

/**
 * The shared frame both the review flow and the edit flow render their editor inside,
 * so reviewing a queued item and editing a reviewed one look and work the same way.
 *
 * It is a sticky glass header — the item's title, its context badges and the persistent
 * "View PDF" control — over a glass `Card` body holding the editor's sections. The
 * header is `sticky top-0` within the scrolling content column, so the title and the
 * PDF control stay in view as the Operator works down a long form, on a phone as on a
 * wide screen. The two flows pass their own title, badges and body; everything else is
 * identical, which is the point — an Operator who can review can edit without relearning
 * the screen.
 *
 * The `View PDF` control lives here rather than in a page header, which is what keeps it
 * visible while the form is on screen: the form is a page, not a modal, so nothing is
 * layered over this header.
 */
export function ItemEditorFrame({
  title,
  badges,
  bookletUrl,
  children,
}: {
  readonly title: string;
  readonly badges?: ReactNode;
  readonly bookletUrl: string;
  readonly children: ReactNode;
}): ReactElement {
  return (
    // This column carries no transform: `position: sticky` resolves against its nearest
    // transformed ancestor, so a transform here (or on an ancestor) would make the header
    // stick within this column and scroll away rather than within the scrolling content
    // area. That is why the page-change transition in the shell is a fade, not a rise —
    // a rise would leave a settled `transform` on an ancestor and un-stick this header.
    <Stack gap="medium">
      {/*
        The header follows the Operator down the form so the "View PDF" control is
        reachable at every scroll position. The *positioning* and the *material* are
        split across two elements on purpose: the glass `panel-glass` sets
        `position: relative` for its sheen pseudo-element, which would override a `sticky`
        on the same element (both set `position`, and the rule wins) — the regression that
        quietly un-stuck this header. So the sticky lives on a plain wrapper and the glass
        Card sits inside it: the wrapper is `sticky top-0 z-20` within the scrolling
        content column (above the body, below the shell's app-bar), and the Card is the
        header's material, the same glass as the editor body beneath so the two read as
        one cohesive unit. The title is left-aligned at the display face, the badges sit
        inline beneath it, and the control aligns to the end on a wide screen and drops
        full-width below the title on a phone.
      */}
      <div className="sticky top-0 z-20">
        <Card className="panel-glass">
          <CardContent>
            <Stack
              direction="responsive"
              gap="medium"
              className="items-start justify-between md:items-center"
            >
              <Stack gap="small" className="min-w-0">
                <h1 className="min-w-0 truncate font-hurdl-display text-hurdl-heading font-hurdl-semibold">
                  {title}
                </h1>
                {badges !== undefined ? (
                  <Stack direction="horizontal" gap="small" align="centre">
                    {badges}
                  </Stack>
                ) : undefined}
              </Stack>
              <div className="w-full shrink-0 md:w-auto">
                <ViewBookletDialog bookletUrl={bookletUrl} />
              </div>
            </Stack>
          </CardContent>
        </Card>
      </div>

      {children}
    </Stack>
  );
}

/** The extraction provenance a reviewer reads at the top of an editor, both flows. */
function ExtractedFrom({
  name,
  category,
}: {
  readonly name: string;
  readonly category?: string;
}): ReactElement {
  return (
    <p className="text-hurdl-caption text-muted-foreground">
      Extracted as &ldquo;{name}&rdquo;
      {category !== undefined && category !== '' ? ` under “${category}”` : ''}
    </p>
  );
}

/**
 * The editor for a queued item.
 *
 * A new item takes a Category from the managed set and publishes under it; a
 * significant change shows the allergen before/after and takes a confirm/reject. Either
 * may carry the same display, nutrition and image edits a reviewed item takes, so an
 * Operator reviews and edits in one submission.
 */
export function QueueEditor({
  item,
  categories,
  onSubmit,
  onCancel,
}: {
  readonly item: ReviewItem;
  readonly categories: ReadonlyArray<Category>;
  readonly onSubmit: (submission: ReviewSubmission) => void;
  /** Abandon the review without submitting. The call site decides where it leads. */
  readonly onCancel?: () => void;
}): ReactElement {
  const [category, setCategory] = useState('');
  const [display, setDisplay] = useState<DisplayDraft>(emptyDisplay);
  const [figures, setFigures] = useState<Record<string, string>>({});
  const [force, setForce] = useState(false);
  const [image, setImage] = useState<ChosenImage | undefined>(undefined);

  // The per-field review decisions, keyed by field, and the image decision — each
  // resolved independently (R5.2, R6). A field with no entry here is still pending.
  const [fieldDecisions, setFieldDecisions] = useState<
    Record<string, ChangeDecision>
  >({});
  const [imageDecision, setImageDecision] = useState<
    ChangeDecision | undefined
  >(undefined);

  const isNew = item.reason === 'new-item';
  const isImageChange = item.reason === 'image-change';

  const submit = (): void => {
    const fieldResolutions = item.changes
      .map((change) => ({ change, decision: fieldDecisions[change.field] }))
      .filter(
        (entry): entry is { change: FieldChange; decision: ChangeDecision } =>
          entry.decision !== undefined,
      )
      .map((entry) => ({
        field: entry.change.field,
        decision: entry.decision,
      }));

    const submission: ReviewSubmission = {
      ...(isNew
        ? { decision: { kind: 'categorise', category: category.trim() } }
        : {}),
      ...(fieldResolutions.length > 0 ? { fieldResolutions } : {}),
      ...(imageDecision !== undefined
        ? { imageResolution: imageDecision }
        : {}),
      ...displayPart(display),
      ...nutritionPart(figures, force),
      ...imagePart(image),
    };
    onSubmit(submission);
  };

  // The primary action is enabled where there is something to submit: a new item needs
  // a category; a significant change needs at least one field decided; an image change
  // needs the image decided.
  const canSubmit = isNew
    ? category.trim() !== ''
    : isImageChange
      ? imageDecision !== undefined
      : Object.keys(fieldDecisions).length > 0;

  return (
    <Card className="panel-glass">
      <CardContent>
        <Stack gap="large">
          <ExtractedFrom
            name={item.extracted.name}
            category={item.extracted.category}
          />

          {isNew ? (
            <CategorySection
              value={category}
              onChange={setCategory}
              categories={categories}
            />
          ) : undefined}

          {item.changes.length > 0 ? (
            <FieldChangesSection
              changes={item.changes}
              decisions={fieldDecisions}
              onDecide={(field, decision) => {
                setFieldDecisions((prior) => ({ ...prior, [field]: decision }));
              }}
            />
          ) : undefined}

          {item.imageChange !== undefined ? (
            <ImageReviewSection
              imageChange={item.imageChange}
              decision={imageDecision}
              onDecide={setImageDecision}
            />
          ) : undefined}

          <DisplaySection
            draft={display}
            onChange={setDisplay}
            categories={categories}
            categoryPlaceholder={item.extracted.category}
            namePlaceholder={item.extracted.name}
          />

          <NutritionSection
            figures={figures}
            onFigures={setFigures}
            force={force}
            onForce={setForce}
          />

          <ImageSection image={image} onImage={setImage} />

          <Stack direction="horizontal" gap="small">
            <Button variant="primary" disabled={!canSubmit} onClick={submit}>
              {isNew ? 'Categorise and publish' : 'Apply decisions'}
            </Button>
            {onCancel !== undefined ? (
              <Button variant="secondary" onClick={onCancel}>
                Cancel
              </Button>
            ) : undefined}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

/** The editor for an already-reviewed item: display, allergens, nutrition, image. */
export function ReviewedEditor({
  item,
  categories,
  onSubmit,
  onCancel,
}: {
  readonly item: ReviewedItem;
  readonly categories: ReadonlyArray<Category>;
  readonly onSubmit: (submission: ReviewSubmission) => void;
  /** Abandon the edit without saving. The call site decides where it leads. */
  readonly onCancel?: () => void;
}): ReactElement {
  const [display, setDisplay] = useState<DisplayDraft>(() => ({
    displayName: item.displayName ?? '',
    displayCategory: item.displayCategory ?? '',
    displaySubcategory: item.displaySubcategory ?? '',
    description: item.description ?? '',
  }));
  const [chosen, setChosen] = useState<Record<string, AllergenValue>>(() =>
    Object.fromEntries(item.allergens.map((a) => [a.key, a.resolved])),
  );
  const [figures, setFigures] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      NUTRIENT_FIELDS.map(({ key }) => [
        key,
        item.nutrition?.[key] !== undefined ? String(item.nutrition[key]) : '',
      ]),
    ),
  );
  const [force, setForce] = useState(false);
  const [image, setImage] = useState<ChosenImage | undefined>(undefined);

  const submit = (): void => {
    const corrections: ReadonlyArray<AllergenCorrectionEdit> = item.allergens
      .filter((allergen) => chosen[allergen.key] !== allergen.extracted)
      .map((allergen) => ({
        allergen: allergen.key,
        to: chosen[allergen.key] ?? allergen.extracted,
      }));

    const submission: ReviewSubmission = {
      ...displayPart(display),
      ...(corrections.length > 0 ? { corrections } : {}),
      ...nutritionPart(figures, force),
      ...imagePart(image),
    };
    onSubmit(submission);
  };

  return (
    <Card className="panel-glass">
      <CardContent>
        <Stack gap="large">
          <ExtractedFrom
            name={item.extractedName}
            category={item.extractedCategory}
          />

          <DisplaySection
            draft={display}
            onChange={setDisplay}
            categories={categories}
            categoryPlaceholder={item.extractedCategory}
            namePlaceholder={item.extractedName}
          />

          <ReviewedAllergenSection
            item={item}
            chosen={chosen}
            onChosen={setChosen}
          />

          <NutritionSection
            figures={figures}
            onFigures={setFigures}
            force={force}
            onForce={setForce}
          />

          <ImageSection image={image} onImage={setImage} />

          <Stack direction="horizontal" gap="small">
            <Button variant="primary" onClick={submit}>
              Save changes
            </Button>
            {onCancel !== undefined ? (
              <Button variant="secondary" onClick={onCancel}>
                Cancel
              </Button>
            ) : undefined}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

/** The display-field draft an editor holds while an Operator types. */
export interface DisplayDraft {
  readonly displayName: string;
  readonly displayCategory: string;
  readonly displaySubcategory: string;
  readonly description: string;
}

const emptyDisplay: DisplayDraft = {
  displayName: '',
  displayCategory: '',
  displaySubcategory: '',
  description: '',
};

/** An image an Operator chose, read into memory as base64 with its media type. */
export interface ChosenImage {
  readonly fileName: string;
  readonly mediaType: ImageMediaType;
  readonly contentBase64: string;
}

/** The display part of a submission, present only where a field was set. */
export function displayPart(
  draft: DisplayDraft,
): Pick<ReviewSubmission, 'display'> {
  const display = {
    ...(draft.displayName !== '' ? { displayName: draft.displayName } : {}),
    ...(draft.displayCategory !== ''
      ? { displayCategory: draft.displayCategory }
      : {}),
    ...(draft.displaySubcategory !== ''
      ? { displaySubcategory: draft.displaySubcategory }
      : {}),
    ...(draft.description !== '' ? { description: draft.description } : {}),
  };
  return Object.keys(display).length > 0 ? { display } : {};
}

/** The nutrition part of a submission, present only where a figure was entered. */
export function nutritionPart(
  figures: Record<string, string>,
  force: boolean,
): Pick<ReviewSubmission, 'nutrition'> {
  const parsed: Record<string, number> = {};
  for (const { key } of NUTRIENT_FIELDS) {
    const raw = figures[key];
    if (raw !== undefined && raw !== '') {
      const value = Number(raw);
      if (!Number.isNaN(value)) parsed[key] = value;
    }
  }
  return Object.keys(parsed).length > 0
    ? { nutrition: { figures: parsed, force } }
    : {};
}

/** The image part of a submission, present only where one was chosen. */
export function imagePart(
  image: ChosenImage | undefined,
): Pick<ReviewSubmission, 'image'> {
  return image === undefined
    ? {}
    : {
        image: {
          mediaType: image.mediaType,
          contentBase64: image.contentBase64,
        },
      };
}

/** The category picker for a new item: a type-to-filter Combobox over managed names. */
export function CategorySection({
  value,
  onChange,
  categories,
}: {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly categories: ReadonlyArray<Category>;
}): ReactElement {
  const headingId = useId();
  return (
    <section aria-labelledby={headingId}>
      <Stack gap="small">
        <h3
          id={headingId}
          className="text-hurdl-subheading font-hurdl-semibold"
        >
          Category
        </h3>
        <CategoryCombobox
          value={value}
          onChange={onChange}
          categories={categories}
          label="Category"
        />
      </Stack>
    </section>
  );
}

/** The display-field editor, the website category chosen from the managed set. */
export function DisplaySection({
  draft,
  onChange,
  categories,
  categoryPlaceholder,
  namePlaceholder,
}: {
  readonly draft: DisplayDraft;
  readonly onChange: (draft: DisplayDraft) => void;
  readonly categories: ReadonlyArray<Category>;
  readonly categoryPlaceholder: string;
  readonly namePlaceholder: string;
}): ReactElement {
  const headingId = useId();
  const nameId = useId();
  const subcategoryId = useId();
  const descriptionId = useId();

  return (
    <section aria-labelledby={headingId}>
      <Stack gap="medium">
        <h3
          id={headingId}
          className="text-hurdl-subheading font-hurdl-semibold"
        >
          Display
        </h3>
        <Stack gap="small">
          <Label htmlFor={nameId}>Display name</Label>
          <Input
            id={nameId}
            value={draft.displayName}
            placeholder={namePlaceholder}
            onChange={(event) => {
              onChange({ ...draft, displayName: event.target.value });
            }}
          />
        </Stack>
        <Stack gap="small">
          <Label>Website category</Label>
          <CategoryCombobox
            value={draft.displayCategory}
            onChange={(next) => {
              onChange({ ...draft, displayCategory: next });
            }}
            categories={categories}
            label="Website category"
            placeholder={categoryPlaceholder}
          />
        </Stack>
        <Stack gap="small">
          <Label htmlFor={subcategoryId}>Subcategory</Label>
          <Input
            id={subcategoryId}
            value={draft.displaySubcategory}
            onChange={(event) => {
              onChange({ ...draft, displaySubcategory: event.target.value });
            }}
          />
        </Stack>
        <Stack gap="small">
          <Label htmlFor={descriptionId}>Description</Label>
          <Input
            id={descriptionId}
            value={draft.description}
            onChange={(event) => {
              onChange({ ...draft, description: event.target.value });
            }}
          />
        </Stack>
      </Stack>
    </section>
  );
}

/** A Category picker built on the Design_System Combobox, filtering the managed set. */
export function CategoryCombobox({
  value,
  onChange,
  categories,
  label,
  placeholder,
}: {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly categories: ReadonlyArray<Category>;
  readonly label: string;
  readonly placeholder?: string;
}): ReactElement {
  const [input, setInput] = useState(value);
  const [open, setOpen] = useState(false);

  // The managed Categories, as the options the Combobox filters. A Category is chosen
  // and sent by name — the backend validates the chosen `displayCategory` against the
  // Restaurant's managed set.
  const options = useMemo<ReadonlyArray<ComboboxOption>>(
    () =>
      categories.map((category) => ({
        value: category.name,
        label: category.name,
      })),
    [categories],
  );

  return (
    <Combobox
      options={options}
      value={value}
      inputValue={input}
      open={open}
      onOpenChange={setOpen}
      onValueChange={(next) => {
        onChange(next);
        setInput(next);
      }}
      onInputValueChange={(next) => {
        setInput(next);
        onChange(next);
      }}
    >
      <ComboboxInput
        aria-label={label}
        placeholder={placeholder ?? 'Choose a category'}
      />
      <ComboboxContent>
        {(matches) =>
          matches.length === 0 ? (
            <ComboboxEmpty>No matching category.</ComboboxEmpty>
          ) : (
            matches.map((option) => (
              <ComboboxItem key={option.value} option={option} />
            ))
          )
        }
      </ComboboxContent>
    </Combobox>
  );
}

/** How a {@link FieldValue} reads in a before/after cell. */
function fieldValueText(value: FieldValue | undefined): string {
  if (value === undefined) return '\u2014';
  if (value.kind === 'allergen') return value.presence;
  if (value.kind === 'nutrient') return String(value.amount);
  return value.text === '' ? '\u2014' : value.text;
}

/** The direction of an allergen change — gained is the one that most needs attention. */
type ChangeKind = 'gained' | 'reduced' | 'added' | 'removed' | 'changed';

/** The rank of an allergen presence, for judging a gain against a reduction (R7.3). */
const PRESENCE_RANK: Readonly<Record<string, number>> = {
  none: 0,
  'may-contain': 1,
  contains: 2,
};

/**
 * Classifies a field change for the highlight (R7.2, R7.3).
 *
 * An allergen gained (a presence rank going up — `none`→`contains`, say) is the change
 * that most needs attention and is marked `gained`; a reduction is `reduced`. A newly
 * present field is `added`, a disappeared one `removed`, anything else `changed`.
 */
function classifyChange(change: FieldChange): ChangeKind {
  if (change.held === undefined) return 'added';
  if (change.held.kind === 'allergen' && change.acquired.kind === 'allergen') {
    const before = PRESENCE_RANK[change.held.presence] ?? 0;
    const after = PRESENCE_RANK[change.acquired.presence] ?? 0;
    if (after > before) return 'gained';
    if (after < before) return 'reduced';
  }
  return 'changed';
}

/**
 * The highlight styling for a change kind — colour **and** an icon/label, never colour
 * alone (R7.2, WCAG 1.4.1).
 *
 * A gained allergen takes the destructive emphasis because it is the safety-critical
 * direction (R7.3); a reduction and a removal take the muted/positive tones; an added
 * or changed field the accent. Each pairs a tone class with a word, so the signal is
 * perceivable without colour.
 */
function highlightOf(kind: ChangeKind): {
  readonly tone: string;
  readonly label: string;
  readonly icon: typeof AlertTriangle;
} {
  switch (kind) {
    case 'gained':
      return {
        tone: 'text-destructive',
        label: 'allergen gained',
        icon: AlertTriangle,
      };
    case 'reduced':
      return {
        tone: 'text-muted-foreground',
        label: 'reduced',
        icon: ArrowRight,
      };
    case 'added':
      return { tone: 'text-hurdl-primary', label: 'added', icon: ArrowRight };
    case 'removed':
      return {
        tone: 'text-muted-foreground',
        label: 'removed',
        icon: ArrowRight,
      };
    case 'changed':
      return { tone: 'text-hurdl-primary', label: 'changed', icon: ArrowRight };
  }
}

/**
 * The per-field changes of a significant change, each with its before → after and its
 * own accept/reject (R5.1, R5.2, R7.1).
 *
 * Each row shows the field, a colour-and-icon highlight of what changed and in which
 * direction (R7.2, R7.3), the held value → the acquired value, and an Accept/Reject
 * pair that records this field's decision independently of the others (R5.2). A row
 * whose decision is set reads it back, so the operator sees what they have resolved
 * before applying.
 */
export function FieldChangesSection({
  changes,
  decisions,
  onDecide,
}: {
  readonly changes: ReadonlyArray<FieldChange>;
  readonly decisions: Readonly<Record<string, ChangeDecision>>;
  readonly onDecide: (field: string, decision: ChangeDecision) => void;
}): ReactElement {
  const headingId = useId();
  return (
    <section aria-labelledby={headingId}>
      <Stack gap="medium">
        <h3
          id={headingId}
          className="text-hurdl-subheading font-hurdl-semibold"
        >
          Changes to review
        </h3>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Field</TableHead>
              <TableHead>Change</TableHead>
              <TableHead>Decision</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {changes.map((change) => {
              const kind = classifyChange(change);
              const highlight = highlightOf(kind);
              const decision = decisions[change.field];
              return (
                <TableRow key={change.field} data-change-kind={kind}>
                  <TableCell>
                    <Stack
                      direction="horizontal"
                      gap="small"
                      align="centre"
                      className={highlight.tone}
                    >
                      <Icon icon={highlight.icon} size="small" />
                      <span className="font-hurdl-medium">{change.field}</span>
                    </Stack>
                    <span className={`text-hurdl-caption ${highlight.tone}`}>
                      {highlight.label}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Stack direction="horizontal" gap="small" align="centre">
                      <span className="text-muted-foreground">
                        {fieldValueText(change.held)}
                      </span>
                      <Icon icon={ArrowRight} size="small" />
                      <span className={`font-hurdl-medium ${highlight.tone}`}>
                        {fieldValueText(change.acquired)}
                      </span>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <DecisionButtons
                      label={change.field}
                      decision={decision}
                      onDecide={(next) => {
                        onDecide(change.field, next);
                      }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Stack>
    </section>
  );
}

/** An accept/reject pair for one change, reading back the decision once made (R5.2). */
function DecisionButtons({
  label,
  decision,
  onDecide,
}: {
  readonly label: string;
  readonly decision: ChangeDecision | undefined;
  readonly onDecide: (decision: ChangeDecision) => void;
}): ReactElement {
  return (
    <Stack direction="horizontal" gap="small" align="centre">
      <Button
        variant={decision === 'confirm' ? 'primary' : 'secondary'}
        size="small"
        aria-label={`Accept ${label}`}
        aria-pressed={decision === 'confirm'}
        onClick={() => {
          onDecide('confirm');
        }}
      >
        <Icon icon={Check} size="small" />
        Accept
      </Button>
      <Button
        variant={decision === 'reject' ? 'destructive' : 'secondary'}
        size="small"
        aria-label={`Reject ${label}`}
        aria-pressed={decision === 'reject'}
        onClick={() => {
          onDecide('reject');
        }}
      >
        <Icon icon={X} size="small" />
        Reject
      </Button>
    </Stack>
  );
}

/**
 * The image review: the current image beside the proposed one, with an accept/reject
 * (R6.1, R6.2, R6.6).
 *
 * The current image is always shown where the item has one, so the reviewer sees what
 * the public sees; the proposed image sits beside it for a side-by-side judgement, and
 * the Accept/Reject records the image decision on its own (R6.5). An item with no
 * current image shows the proposed one alone against an "no current image" note.
 */
export function ImageReviewSection({
  imageChange,
  decision,
  onDecide,
}: {
  readonly imageChange: ImageChangeView;
  readonly decision: ChangeDecision | undefined;
  readonly onDecide: (decision: ChangeDecision) => void;
}): ReactElement {
  const headingId = useId();
  return (
    <section aria-labelledby={headingId}>
      <Stack gap="medium">
        <h3
          id={headingId}
          className="text-hurdl-subheading font-hurdl-semibold"
        >
          Image change
        </h3>
        <Stack direction="responsive" gap="medium">
          <Stack gap="small" className="min-w-0">
            <span className="text-hurdl-caption text-muted-foreground">
              Current
            </span>
            {imageChange.currentImageUrl !== undefined ? (
              <img
                src={imageChange.currentImageUrl}
                alt="The image currently shown for this item"
                className="w-full max-w-hurdl-xs rounded-hurdl-medium border border-border"
              />
            ) : (
              <p className="text-hurdl-caption text-muted-foreground">
                No current image.
              </p>
            )}
          </Stack>
          <Stack gap="small" className="min-w-0">
            <span className="text-hurdl-caption text-hurdl-primary">
              Proposed
            </span>
            <img
              src={imageChange.proposedImageUrl}
              alt="The newly acquired image proposed for this item"
              className="w-full max-w-hurdl-xs rounded-hurdl-medium border border-hurdl-primary"
            />
          </Stack>
        </Stack>
        <DecisionButtons
          label="image"
          decision={decision}
          onDecide={onDecide}
        />
      </Stack>
    </section>
  );
}

/** The allergen-correction editor for a reviewed item. */
export function ReviewedAllergenSection({
  item,
  chosen,
  onChosen,
}: {
  readonly item: ReviewedItem;
  readonly chosen: Record<string, AllergenValue>;
  readonly onChosen: (
    update: (
      prior: Record<string, AllergenValue>,
    ) => Record<string, AllergenValue>,
  ) => void;
}): ReactElement {
  const headingId = useId();
  return (
    <section aria-labelledby={headingId}>
      <Stack gap="medium">
        <h3
          id={headingId}
          className="text-hurdl-subheading font-hurdl-semibold"
        >
          Allergens
        </h3>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Allergen</TableHead>
              <TableHead>Extracted</TableHead>
              <TableHead>Shown</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {item.allergens.map((allergen) => (
              <TableRow key={allergen.key}>
                <TableCell>{allergen.key}</TableCell>
                <TableCell>{allergen.extracted}</TableCell>
                <TableCell>
                  <AllergenChoice
                    allergen={allergen.key}
                    value={chosen[allergen.key] ?? allergen.resolved}
                    onChange={(value) => {
                      onChosen((prior) => ({
                        ...prior,
                        [allergen.key]: value,
                      }));
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Stack>
    </section>
  );
}

/** The value chooser for one allergen. */
function AllergenChoice({
  allergen,
  value,
  onChange,
}: {
  readonly allergen: string;
  readonly value: AllergenValue;
  readonly onChange: (value: AllergenValue) => void;
}): ReactElement {
  return (
    <Select
      value={value}
      onValueChange={(next) => {
        onChange(next as AllergenValue);
      }}
    >
      <SelectTrigger aria-label={`${allergen} value`}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {ALLERGEN_VALUES.map((candidate) => (
          <SelectItem key={candidate} value={candidate}>
            {candidate}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** The nutrition editor, one numeric field per nutrient and the override toggle. */
export function NutritionSection({
  figures,
  onFigures,
  force,
  onForce,
}: {
  readonly figures: Record<string, string>;
  readonly onFigures: (
    update: (prior: Record<string, string>) => Record<string, string>,
  ) => void;
  readonly force: boolean;
  readonly onForce: (force: boolean) => void;
}): ReactElement {
  const headingId = useId();
  const forceId = useId();
  return (
    <section aria-labelledby={headingId}>
      <Stack gap="medium">
        <h3
          id={headingId}
          className="text-hurdl-subheading font-hurdl-semibold"
        >
          Nutrition
        </h3>
        <Stack gap="small">
          {NUTRIENT_FIELDS.map(({ key, label }) => (
            <NutrientField
              key={key}
              label={label}
              value={figures[key] ?? ''}
              onChange={(value) => {
                onFigures((prior) => ({ ...prior, [key]: value }));
              }}
            />
          ))}
        </Stack>
        <Stack direction="horizontal" gap="small">
          <input
            type="checkbox"
            id={forceId}
            checked={force}
            onChange={(event) => {
              onForce(event.target.checked);
            }}
          />
          <Label htmlFor={forceId}>Override the implausible-change guard</Label>
        </Stack>
      </Stack>
    </section>
  );
}

/** One labelled numeric nutrition input. */
export function NutrientField({
  label,
  value,
  onChange,
}: {
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
}): ReactElement {
  const id = useId();
  return (
    <Stack gap="small">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="number"
        inputMode="decimal"
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      />
    </Stack>
  );
}

/** The image uploader: a Dropzone that reads a chosen image into memory as base64. */
export function ImageSection({
  image,
  onImage,
}: {
  readonly image: ChosenImage | undefined;
  readonly onImage: (image: ChosenImage | undefined) => void;
}): ReactElement {
  const headingId = useId();
  const [problem, setProblem] = useState<string>('');

  const choose = (files: DropzoneFiles): void => {
    const file = files[0];
    if (file === undefined) return;

    const mediaType = acceptedMediaType(file.type);
    if (mediaType === undefined) {
      setProblem('Choose a PNG, JPEG or WebP image.');
      onImage(undefined);
      return;
    }

    void readAsBase64(file).then((contentBase64) => {
      setProblem('');
      onImage({ fileName: file.name, mediaType, contentBase64 });
    });
  };

  return (
    <section aria-labelledby={headingId}>
      <Stack gap="small">
        <h3
          id={headingId}
          className="text-hurdl-subheading font-hurdl-semibold"
        >
          Image
        </h3>
        <Dropzone
          aria-label="Product image"
          accept="image/png,image/jpeg,image/webp"
          onFilesSelected={choose}
        >
          {image === undefined
            ? 'Drop an image here, or choose one.'
            : `Chosen: ${image.fileName}`}
        </Dropzone>
        {problem !== '' ? (
          <p role="alert" className="text-destructive">
            {problem}
          </p>
        ) : undefined}
      </Stack>
    </section>
  );
}

/** The accepted media type for a File's reported type, or undefined where none. */
export function acceptedMediaType(type: string): ImageMediaType | undefined {
  return ACCEPTED_IMAGE_TYPES.find((accepted) => accepted === type);
}

/**
 * Reads a File's bytes as a base64 string (no data-URL prefix), for the upload body.
 *
 * The reader resolves a `data:<type>;base64,<payload>` URL; the payload after the comma
 * is the base64 the backend's image route decodes. The browser's `FileReader` is the
 * one way to read a chosen file's bytes; it reaches no network.
 */
export function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => {
      reject(reader.error ?? new Error('The image could not be read.'));
    };
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('The image could not be read.'));
        return;
      }
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}
